import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Target, ChevronRight, Loader2, Sparkles } from "lucide-react";

/**
 * Card mostrata in Home:
 *  - se l'utente non ha ancora scelto un obiettivo → CTA "Imposta un obiettivo"
 *  - se ce l'ha → mostra icona + titolo + score giornaliero + verdetto
 * Tap → naviga a /obiettivi
 */
export default function GoalHomeCard() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const goalRes = await api.get("/api/goals/me");
        if (cancelled) return;
        if (!goalRes.data?.has_goal) {
          setState({ loading: false, hasGoal: false });
          return;
        }
        // Prova a caricare la valutazione di oggi (opzionale, può non esserci)
        let daily = null;
        try {
          const evRes = await api.get("/api/goals/me/daily-evaluation");
          daily = evRes.data;
        } catch {
          /* ignore */
        }
        if (cancelled) return;
        setState({
          loading: false,
          hasGoal: true,
          goal: goalRes.data.goal,
          daily,
        });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (state.loading) {
    return (
      <div
        data-testid="goal-home-card-loading"
        className="mt-4 rounded-[22px] p-4 flex items-center gap-3"
        style={{
          background: "linear-gradient(135deg, #1e1a3b 0%, #2a1f52 100%)",
          border: "1px solid rgba(167,139,250,0.2)",
        }}
      >
        <Loader2 size={18} className="animate-spin text-violet-300" />
        <span className="text-white/60 text-[13px]">Carico il tuo obiettivo…</span>
      </div>
    );
  }

  if (state.error) return null;

  // No goal yet — CTA setup
  if (!state.hasGoal) {
    return (
      <button
        data-testid="goal-home-card-cta"
        onClick={() => navigate("/obiettivi")}
        className="mt-4 w-full rounded-[22px] p-4 text-left transition-transform hover:-translate-y-0.5 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #4c1d95 0%, #7c3aed 55%, #a78bfa 100%)",
          boxShadow: "0 16px 40px -20px rgba(139,92,246,0.5)",
        }}
      >
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/15 blur-2xl pointer-events-none" />
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center">
            <Target size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1.5 text-white/85 text-[10.5px] uppercase tracking-[0.15em] mb-1">
              <Sparkles size={11} /> Nuovo
            </div>
            <div className="text-white text-[16px] font-semibold leading-tight">
              Scegli un obiettivo
            </div>
            <div className="text-white/85 text-[12px] mt-0.5 leading-snug">
              Piano personalizzato basato su fonti scientifiche verificate
            </div>
          </div>
          <ChevronRight size={20} className="text-white/70 mt-2" />
        </div>
      </button>
    );
  }

  // Goal impostato → mini-card con verdict
  const goal = state.goal;
  const daily = state.daily;
  const score = daily?.has_data ? Math.round(daily.overall_score) : null;
  const verdict = daily?.verdict;
  const verdictLabel = {
    ottimo: "Ottimo",
    buono: "Buono",
    da_migliorare: "Da migliorare",
    insufficiente: "Insufficiente",
  }[verdict] || "In attesa dati";

  const scoreColor =
    score == null
      ? "text-white/60"
      : score >= 85
      ? "text-emerald-300"
      : score >= 70
      ? "text-teal-300"
      : score >= 50
      ? "text-amber-300"
      : "text-rose-300";

  return (
    <button
      data-testid="goal-home-card-active"
      onClick={() => navigate("/obiettivi")}
      className="mt-4 w-full rounded-[22px] p-4 text-left transition-transform hover:-translate-y-0.5 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${goal.goal_snapshot?.color || "#7C3AED"}20 0%, #0a0a2a 100%)`,
        border: `1px solid ${goal.goal_snapshot?.color || "#7C3AED"}55`,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center"
          style={{
            background: `${goal.goal_snapshot?.color || "#7C3AED"}30`,
            border: `1px solid ${goal.goal_snapshot?.color || "#7C3AED"}66`,
          }}
        >
          <Target size={20} style={{ color: goal.goal_snapshot?.color || "#a78bfa" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white/60 text-[10.5px] uppercase tracking-[0.15em]">
            Il tuo obiettivo
          </div>
          <div className="text-white text-[15px] font-semibold leading-tight truncate">
            {goal.goal_snapshot?.title}
          </div>
          <div className={`text-[12px] mt-0.5 ${scoreColor}`}>
            {score != null ? `${score}/100 · ${verdictLabel}` : verdictLabel}
          </div>
        </div>
        <ChevronRight size={20} className="text-white/60" />
      </div>
    </button>
  );
}
